import * as React from "react";
import { casesActions } from "configs";
import {
  setErrorsFromResponse,
  useCurrentCase,
  useDispatch,
  useParams
} from "duck";
import { useUpdateDentalChartingMutation } from "requests";
import { useDentalChart } from "../../../../../duck";

export const useLogTime = () => {
  const dispatch = useDispatch();
  const shouldSync = useCurrentCase(state => state.shouldSync);
  const dentalChart = useDentalChart();

  const { id: caseId, toothNumber } = useParams();

  const chartId = dentalChart?._id || null;
  const oldTimeSec = dentalChart?.timerDurationSec || 0;
  const oldTimeRef = React.useRef(oldTimeSec);
  oldTimeRef.current = oldTimeSec;

  const [update] = useUpdateDentalChartingMutation({ ignoreResults: true });

  return React.useCallback(
    (increment: number) => {
      dispatch(
        casesActions.incrementDentalTimer({
          _id: caseId,
          data: { toothNumber: +toothNumber, increment }
        })
      );

      if (chartId && shouldSync) {
        update({
          variables: {
            input: {
              _id: chartId,
              timerDurationSec: oldTimeRef.current + increment
            }
          }
        }).catch(error => setErrorsFromResponse(error));
      }
    },
    [shouldSync, chartId]
  );
};

export const useTimer = () => {
  const logTime = useLogTime();

  const [extraSec, setExtraSec] = React.useState(0);

  const timerId = React.useRef<number | null>(null);
  const [running, setRunning] = React.useState(false);

  const onStart = () => {
    timerId.current = window.setInterval(() => {
      setExtraSec(prev => prev + 1);
    }, 1000);
    setRunning(true);
  };

  const onStop = () => {
    if (timerId.current) {
      clearInterval(timerId.current);
      timerId.current = null;
    }

    logTime(extraSec);
    setExtraSec(0);

    setRunning(false);
  };

  const mounted = React.useRef(true);
  React.useLayoutEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  React.useLayoutEffect(() => {
    if (extraSec === 15) {
      setExtraSec(0);
      logTime(15);
    }

    return () => {
      if (mounted.current) {
        return;
      }

      if (extraSec !== 0) {
        logTime(extraSec);
      }

      if (timerId.current) {
        clearInterval(timerId.current);
      }
    };
  }, [logTime, extraSec]);

  return {
    extraSec,
    running,
    toggle() {
      if (running) {
        onStop();
      } else {
        onStart();
      }
    }
  };
};

export const usePreventReload = ({ enabled }: { enabled: boolean }) => {
  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const handler = (event: Event) => {
      event.preventDefault();
      event.returnValue = true;
    };

    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [enabled]);
};
